import { useEffect, useRef } from "react";

/**
 * useScrollReveal — Observes .reveal elements and adds "visible" class
 * when they enter the viewport.
 *
 * Features:
 * - Respects prefers-reduced-motion (immediately shows all elements)
 * - Re-initialises when theme/language changes (React re-renders)
 * - Uses a short RAF delay to ensure React has committed DOM nodes
 * - Cleans up observers on unmount
 * - Threshold: 8% visibility required to trigger (good for large cards)
 */
const useScrollReveal = (theme, language) => {
  const observerRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const init = () => {
      // If user prefers reduced motion, immediately reveal everything
      if (prefersReducedMotion) {
        document.querySelectorAll(".reveal").forEach((el) => {
          el.classList.add("visible");
        });
        return () => {};
      }

      // Disconnect any previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              // Once visible, unobserve to save resources
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.08,
          rootMargin: "0px 0px -32px 0px",
        }
      );

      const elements = document.querySelectorAll(".reveal");
      elements.forEach((el) => {
        // Only observe elements that aren't already visible
        if (!el.classList.contains("visible")) {
          observerRef.current.observe(el);
        }
      });

      return () => {
        observerRef.current?.disconnect();
      };
    };

    // Allow React to commit DOM before querying .reveal elements
    const timer = setTimeout(init, 100);

    return () => {
      clearTimeout(timer);
      observerRef.current?.disconnect();
    };
  }, [theme, language]);
};

export default useScrollReveal;
